import React from "react";
import { Link } from "react-router-dom";
import spottoLogo from "../assets/spotto-logo.png";

const SUPPORT_EMAIL = "support@spotto.pl";
const APP_STORE_URL = "https://apps.apple.com/app/spotto/id000000000";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=pl.spotto.mobile";

const FAQ: { q: string; a: string }[] = [
  {
    q: "Jak zarezerwować obiekt?",
    a: "Wybierz obiekt z listy lub mapy, sprawdź dostępne terminy, wybierz interesującą Cię godzinę i opłać rezerwację.",
  },
  {
    q: "Jak anulować rezerwację?",
    a: "W zakładce „Rezerwacje” wybierz interesującą Cię rezerwację i kliknij „Anuluj”. Zwrot środków następuje zgodnie z polityką zwrotów obiektu.",
  },
  {
    q: "Jak dodać znajomych do rezerwacji grupowej?",
    a: "Po wybraniu obiektu zaznacz „Rezerwacja grupowa” i podaj liczbę uczestników. Możesz zapłacić za wszystkich lub każdy uczestnik opłaca swoją część osobno.",
  },
  {
    q: "Czy moja płatność jest bezpieczna?",
    a: "Tak. Płatności obsługuje certyfikowany operator TPay. Spotto nigdy nie przechowuje danych Twojej karty płatniczej.",
  },
  {
    q: "Jak zostać trenerem na Spotto?",
    a: "Stwórz profil trenera w aplikacji, podaj swoje specjalizacje i ustaw dostępność. Po krótkiej weryfikacji Twój profil będzie widoczny dla użytkowników.",
  },
  {
    q: "Jak dodać swój obiekt sportowy?",
    a: "Skontaktuj się z nami pod adresem support@spotto.pl — pomożemy Ci skonfigurować profil obiektu i ustawić grafik dostępności.",
  },
];

export const Contact: React.FC = () => {
  return (
    <div className="min-h-screen bg-surface-dark">
      <header className="bg-surface-dark-elevated border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-4">
              <img
                src={spottoLogo}
                alt="Spotto"
                className="h-10 w-auto"
              />
              <span className="font-display text-xl font-semibold text-ink-on-dark tracking-tight">
                Spotto
              </span>
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-ink-on-dark-muted hover:text-ink-on-dark transition-colors"
            >
              Powrót na stronę główną
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand-primary mb-3">
            Pomoc i kontakt
          </p>
          <h1 className="font-display text-4xl lg:text-5xl font-light text-ink-on-dark mb-4 tracking-tight">
            Jak możemy pomóc?
          </h1>
          <p className="text-base text-ink-on-dark-muted max-w-2xl mx-auto">
            Skontaktuj się z nami — odpowiemy w ciągu 24 godzin w dni robocze.
          </p>
        </div>

        {/* Contact card */}
        <section className="bg-surface-dark-elevated rounded-2xl border border-white/10 p-8 mb-8 shadow-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-on-dark-muted mb-2">
                Email
              </p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-lg font-semibold text-brand-primary hover:text-brand-primary-hover transition-colors"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-on-dark-muted mb-2">
                Godziny wsparcia
              </p>
              <p className="text-base text-ink-on-dark">
                pon.–pt., 9:00–17:00 (CET)
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-8">
          <h2 className="font-display text-2xl font-semibold text-ink-on-dark mb-6 tracking-tight">
            Najczęstsze pytania
          </h2>
          <div className="space-y-3">
            {FAQ.map((item, idx) => (
              <details
                key={idx}
                className="group bg-surface-dark-elevated rounded-xl border border-white/10 overflow-hidden"
              >
                <summary className="cursor-pointer px-6 py-4 flex items-center justify-between hover:bg-white/[0.03] transition-colors">
                  <span className="font-medium text-ink-on-dark pr-4">
                    {item.q}
                  </span>
                  <span className="text-ink-on-dark-muted text-xl leading-none group-open:rotate-45 transition-transform">
                    +
                  </span>
                </summary>
                <div className="px-6 pb-5 text-ink-on-dark-muted leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </section>

        {/* Account & Privacy */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Link
            to="/delete-account"
            className="bg-surface-dark-elevated rounded-xl border border-white/10 p-6 hover:border-brand-accent/40 transition-colors group"
          >
            <h3 className="font-semibold text-ink-on-dark mb-1 group-hover:text-brand-accent transition-colors">
              Usuwanie konta
            </h3>
            <p className="text-sm text-ink-on-dark-muted">
              Usuń swoje konto i wszystkie powiązane dane zgodnie z RODO.
            </p>
          </Link>
          <Link
            to="/privacy-policy"
            className="bg-surface-dark-elevated rounded-xl border border-white/10 p-6 hover:border-brand-primary/40 transition-colors group"
          >
            <h3 className="font-semibold text-ink-on-dark mb-1 group-hover:text-brand-primary transition-colors">
              Polityka prywatności
            </h3>
            <p className="text-sm text-ink-on-dark-muted">
              Jak zbieramy, przechowujemy i wykorzystujemy Twoje dane.
            </p>
          </Link>
        </section>

        {/* App download */}
        <section className="bg-surface-dark-elevated rounded-2xl border border-white/10 p-8 mb-8 text-center">
          <h2 className="font-display text-2xl font-semibold text-ink-on-dark mb-3 tracking-tight">
            Pobierz aplikację Spotto
          </h2>
          <p className="text-sm text-ink-on-dark-muted mb-6">
            Dostępna na iOS i Android.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/[0.05] border border-white/[0.10] hover:bg-white/[0.08] hover:border-white/[0.20] text-ink-on-dark text-sm font-semibold transition-colors"
            >
              <span aria-hidden></span>
              <span>App Store</span>
            </a>
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/[0.05] border border-white/[0.10] hover:bg-white/[0.08] hover:border-white/[0.20] text-ink-on-dark text-sm font-semibold transition-colors"
            >
              <span aria-hidden>▶</span>
              <span>Google Play</span>
            </a>
          </div>
        </section>

        {/* Footer / company info */}
        <footer className="text-center text-xs text-ink-on-dark-muted/70 leading-relaxed pb-8">
          <p>Spotto / Solid Labs PSA</p>
          <p>
            <a
              href="https://spotto.pl"
              className="hover:text-ink-on-dark-muted transition-colors"
            >
              spotto.pl
            </a>
          </p>
        </footer>
      </main>
    </div>
  );
};
